
    import { defineComponent } from 'vue'
    import { AxiosResponse } from 'axios'
    import { iAttribute, iLov } from './ProfileAttributesManagement'
    import useValidate from '@vuelidate/core'
    import { createValidations, ICustomValidatorMap } from '@/helpers/commons/validationHelper'
    import Dropdown from 'primevue/dropdown'
    import InputSwitch from 'primevue/inputswitch'
    import RadioButton from 'primevue/radiobutton'
    import KnValidationMessages from '@/components/UI/KnValidatonMessages.vue'
    import ProfileAttributesManagementDescriptor from './ProfileAttributesManagementDescriptor.json'
    import profileAttributesManagementValidationDescriptor from './ProfileAttributesManagementValidationDescriptor.json'

    export default defineComponent({
        name: 'profile-attributes-detail',
        components: {
            Dropdown,
            InputSwitch,
            RadioButton,
            KnValidationMessages
        },
        props: {
            selectedAttribute: {
                type: Object,
                required: true
            }
        },
        computed: {
            formValid(): any {
                return this.v$.$invalid
            }
        },
        watch: {
            selectedAttribute: {
                handler: function(attribute) {
                    this.v$.$reset()
                    this.loadAttribute(attribute)
                }
            }
        },
        emits: ['refreshRecordSet', 'closesForm', 'dataChanged'],
        data() {
            return {
                v$: useValidate() as any,
                apiUrl: process.env.VUE_APP_RESTFUL_SERVICES_PATH + '2.0/',
                attribute: {} as iAttribute,
                lovs: {} as iLov,
                loading: false as Boolean,
                hideForm: false as Boolean,
                isDirty: false as Boolean,
                disableLov: true as Boolean,
                enableLov: true as Boolean,
                LovSelectHidden: true as Boolean,
                syntaxSelectHidden: true as Boolean,
                profileAttributesManagementDescriptor: ProfileAttributesManagementDescriptor,
                columns: ProfileAttributesManagementDescriptor.columns,
                attributeTypeValues: ProfileAttributesManagementDescriptor.attributeTypeValues
            }
        },
        validations() {
            const customValidators: ICustomValidatorMap = {
                'custom-required': (value) => {
                    return this.enableLov || value
                }
            }
            return {
                attribute: createValidations('attribute', profileAttributesManagementValidationDescriptor.validations.attribute, customValidators)
            }
        },
        async created() {
            await this.loadLovs()
            if (this.selectedAttribute) {
                this.loadAttribute(this.selectedAttribute)
            }
        },
        methods: {
            onLoveBlur() {
                this.v$.attribute.lovId.$touch()
            },
            async loadLovs() {
                this.loading = true
                await this.$http
                    .get(this.apiUrl + 'lovs/get/all')
                    .then((response: AxiosResponse<any>) => {
                        this.lovs = response.data
                    })
                    .finally(() => (this.loading = false))
            },
            resetForm() {
                Object.keys(this.attribute).forEach((k) => delete this.attribute[k])
            },
            showForm() {
                this.hideLovDropdown(false)
                this.syntaxSelectHidden = true
                this.resetForm()
                this.hideForm = false
            },
            async save() {
                let response: AxiosResponse<any>
                if (this.attribute.value === 'NUMBER') {
                    this.attribute.value = 'NUM'
                }
                if (this.attribute.attributeId != null) {
                    response = await this.$http.put(this.apiUrl + 'attributes/' + this.attribute.attributeId, this.attribute, ProfileAttributesManagementDescriptor.headers)
                } else {
                    response = await this.$http.post(this.apiUrl + 'attributes/', this.attribute, ProfileAttributesManagementDescriptor.headers)
                }
                if (response.status == 200) {
                    if (response.data.errors) {
                        console.log(response.data.errors)
                    } else {
                        this.$store.commit('setInfo', {
                            title: this.$t('managers.profileAttributesManagement.info.saveTitle'),
                            msg: this.$t('managers.profileAttributesManagement.info.saveMessage')
                        })
                    }
                }

                this.$emit('refreshRecordSet')
                this.resetForm()
            },
            closeForm() {
                this.$emit('closesForm')
            },
            onAttributeSelect(event: any) {
                this.populateForm(event.data)
            },
            checkSyntax() {
                if (this.attribute.multivalue === true) {
                    this.showSyntaxButtons(this.attribute)
                } else {
                    this.attribute.syntax = null
                    this.syntaxSelectHidden = true
                }
            },
            populateForm(attribute: iAttribute) {
                this.hideLovDropdown(false)
                this.hideForm = false

                this.attribute = { ...attribute }

                if (typeof attribute.value === 'object' && attribute.value !== null) {
                    this.attribute.value = attribute.value['type'].toUpperCase()
                }

                this.showSyntaxButtons(attribute)

                if (attribute.lovId !== null) {
                    this.disableLovs()
                    this.showLovDropdown(false)
                } else {
                    this.enableLovs()
                }
            },
            showSyntaxButtons(attribute: iAttribute) {
                if (attribute.lovId !== null && attribute.multivalue === true) {
                    this.syntaxSelectHidden = false
                } else {
                    this.syntaxSelectHidden = true
                }
            },
            hideLovDropdown(emitChange = true) {
                this.attribute.lovId = null
                this.enableLovs()
                this.LovSelectHidden = true
                if (emitChange) this.$emit('dataChanged')
            },
            showLovDropdown(emitChange = true) {
                this.disableLovs()
                this.LovSelectHidden = false
                if (emitChange) this.$emit('dataChanged')
            },
            disableLovs() {
                this.enableLov = false
                this.disableLov = true
            },
            enableLovs() {
                this.enableLov = true
                this.disableLov = false
            },
            onDataChange(v$Comp) {
                v$Comp.$touch()
                this.$emit('dataChanged')
            },
            onLoveDropdownChange() {
                this.checkSyntax()
                this.$emit('dataChanged')
            },
            loadAttribute(attribute) {
                if (attribute.attributeId === null) {
                    this.resetForm()
                    return
                }
                this.populateForm(attribute)
            },
            setDirty(v$Comp) {
                v$Comp.$touch()
                this.$emit('dataChanged')
            }
        }
    })
